import { dashboardReportsChartListOptions } from '@crm/api'
import { getColor } from '@crm/components/Charts/utils'
import { useStyles } from '@crm/features/dashboard/styles'
import { ChartType } from '@crm/features/reports'
import { abbreviateNumber } from '@crm/utils'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { DEFAULT_DATE_FORMAT, format } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Card, Empty, Flex, Segmented } from 'antd'
import cn from 'classnames'
import { useEffect } from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

const color = getColor(4)

export const ChartReports = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { params, updateQuery } = useQueryUpdate()

  const { data = [], isLoading } = useQuery({
    ...dashboardReportsChartListOptions({
      query: {
        ...params,
        chart_type: params?.chart_type
      }
    }),
    select: (response) => {
      if (response?.length) {
        return response?.map(({ value, date }) => ({
          value,
          date: format(
            date,
            DEFAULT_DATE_FORMAT.split('.').slice(0, 3).join('.')
          )
        }))
      }
      return []
    }
  })

  const segmentedOptions = Object.values(ChartType)?.map((type) => ({
    value: type,
    label: t(`reports.${type}`)
  }))

  useEffect(() => {
    if (!params?.chart_type) {
      updateQuery({ chart_type: 'sales' })
    }
  }, [params?.chart_type])

  return (
    <Card
      className={cn(
        styles.card,
        styles.dashboardCharts,
        styles.flexGrowDashboardCharts
      )}
      variant="borderless"
      loading={isLoading}
      title={
        <Flex justify="space-between" align="center">
          {t('reports.graphic_reports')}

          <Segmented
            options={segmentedOptions}
            onChange={(value) => updateQuery({ chart_type: value })}
          />
        </Flex>
      }
    >
      {data?.length ? (
        <div className="chart__container pb-0 pl-0">
          <ResponsiveContainer width="100%" height={500}>
            <ComposedChart data={data}>
              <defs>
                <linearGradient id="colorLine1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid vertical={false} strokeDasharray="4 0" />
              <XAxis dataKey="date" scale="band" />
              <YAxis
                yAxisId="left"
                orientation="left"
                scale="auto"
                type="number"
                tickFormatter={(value) => abbreviateNumber(value)}
              />
              <Tooltip
                content={({ active, payload }) =>
                  active && payload
                    ? `${payload[0].payload.date} - ${payload[0].value}`
                    : null
                }
              />
              <Area
                type="monotone"
                yAxisId="left"
                dataKey="value"
                stroke={color}
                fillOpacity={1}
                fill="url(#colorLine1)"
                activeDot={{ stroke: color, strokeWidth: 4 }}
                dot={{ stroke: 'white', strokeWidth: 3, r: 6, fill: color }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Empty />
      )}
    </Card>
  )
}
